import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  MagnifyingGlass, DotsThreeVertical, ChatCircleDots, Gear, SignOut, ArrowClockwise, Users,
} from '@phosphor-icons/react';
import { toast } from 'sonner';
import InstantAccessPT from './InstantAccessPT';
import InstantAccessG from './InstantAccessG';
import InstantAccessN from './InstantAccessN';
import InstantAccessPW from './InstantAccessPW';
import { truncatePreview, truncateName, formatLastMessagePreview } from '../utils/previewTruncate';
import MBSetupBadge from './MBSetupBadge';
import { List as VirtualList } from 'react-window';
import { AutoSizer } from 'react-virtualized-auto-sizer';

/**
 * WhatsApp-style mobile view for Handler PWA. Mirrors MobileWhatsAppAdminView
 * with handler-specific bits: chatTypeTab (only shown for deposit_handler),
 * assigned_mbs restriction on the MB selector, and chat.* payload shape.
 */
const FILTERS = [
  { key: 'all', label: 'All' },
  { key: 'unread', label: 'Unread' },
  { key: 'favourites', label: 'Favourites' },
  { key: 'groups', label: 'Groups' },
];

const AVATAR_PALETTE = [
  { bg: '#DCE7F5', fg: '#2E5AAC' },
  { bg: '#D6EDDA', fg: '#1F6E3A' },
  { bg: '#F7DED0', fg: '#B14A16' },
  { bg: '#EBD9F0', fg: '#6B2E82' },
  { bg: '#FBE1E1', fg: '#B32828' },
  { bg: '#D9EEF2', fg: '#1F6B7C' },
  { bg: '#F4E6C7', fg: '#8B6914' },
  { bg: '#E5E1F5', fg: '#3B3591' },
];

import formatChatTime from '../utils/formatChatTime';
import InquiryPanel from './InquiryPanel';

const MobileWhatsAppHandlerView = ({
  user, isDepositHandler,
  chatTypeTab, setChatTypeTab,
  activeTab, setActiveTab,
  filteredChats,
  unreadCounts,
  searchQuery, setSearchQuery,
  handleChatClick, handleLogout,
  mbFilter, setMbFilter, mbOptions,
  chats,
  unreadTabBadge, favouritesTabBadge, groupsTabBadge,
  // Selection mode props
  selectionMode, selectedRooms, cancelSelection,
  handleLongPressStart, handleLongPressEnd, handleTouchMove,
  toggleRoomSelection, setShowDeleteConfirm, showDeleteConfirm, handleDeleteRooms,
  handleToggleArchive, handleToggleFavourite,
  onRefresh,
  API,
}) => {
  const navigate = useNavigate();
  // Enquiry slide → render the shared InquiryPanel (filter + list) instead of
  // the normal chat list, which was empty ("No chats yet") + had no date filter.
  const isInquirySection = chatTypeTab === 'inquiry' || user?.chat_type === 'inquiry';
  const [showMenu, setShowMenu] = useState(false);
  const [mbOpen, setMbOpen] = useState(false);
  const [mbMenuPos, setMbMenuPos] = useState({ top: 0, left: 0 });
  const menuRef = useRef(null);
  const mbRef = useRef(null);
  const listRef = useRef(null);
  const touchStartX = useRef(null);
  const touchStartY = useRef(null);

  // Restore chat-list scroll position on back-nav (waits for rows to paint)
  useEffect(() => {
    if (!listRef.current) return;
    try {
      const key = `hdl_list_scroll_${chatTypeTab}_${activeTab}`;
      const saved = parseInt(sessionStorage.getItem(key) || '0', 10);
      if (saved > 0) {
        requestAnimationFrame(() => {
          if (listRef.current) listRef.current.scrollTop = saved;
        });
      }
    } catch (_) {}
  }, [activeTab, chatTypeTab, (filteredChats || []).length]);

  const _openChat = (chat) => {
    try {
      if (listRef.current) {
        const key = `hdl_list_scroll_${chatTypeTab}_${activeTab}`;
        sessionStorage.setItem(key, String(listRef.current.scrollTop));
      }
    } catch (_) {}
    handleChatClick(chat);
  };

  // Point 2 — save the OUTGOING panel's scroll BEFORE any tab/section switch so
  // Deposit/Withdrawal/Support each restore their own position (no bleed-over).
  const saveTabScroll = () => {
    try {
      if (listRef.current) sessionStorage.setItem(`hdl_list_scroll_${chatTypeTab}_${activeTab}`, String(listRef.current.scrollTop));
    } catch (_) {}
  };

  useEffect(() => {
    const onClick = (e) => { if (menuRef.current && !menuRef.current.contains(e.target)) setShowMenu(false); };
    if (showMenu) document.addEventListener('click', onClick);
    return () => document.removeEventListener('click', onClick);
  }, [showMenu]);
  useEffect(() => {
    const onClick = (e) => { if (mbRef.current && !mbRef.current.contains(e.target)) setMbOpen(false); };
    if (mbOpen) document.addEventListener('click', onClick);
    return () => document.removeEventListener('click', onClick);
  }, [mbOpen]);

  // Sections available to this handler
  // Session-48i: 4-tab slide layout (Enquiry moved out of the Support
  // sub-tab into its own top-level slide, matching desktop layout).
  const sections = isDepositHandler
    ? [
        { key: 'deposit', label: 'Deposit' },
        { key: 'withdrawal', label: 'Withdrawal' },
        { key: 'support', label: 'Support' },
        { key: 'inquiry', label: 'Enquiry' },
      ]
    : [{ key: user?.chat_type || 'deposit', label: (user?.chat_type === 'inquiry' ? 'Enquiry' : (user?.chat_type || 'deposit').replace(/^./, c => c.toUpperCase())) }];
  const currentIdx = sections.findIndex(s => s.key === (isDepositHandler ? chatTypeTab : sections[0].key));
  const currentSection = sections[currentIdx] || sections[0];

  const goToSection = (idx) => {
    if (!isDepositHandler) return;
    if (idx < 0 || idx >= sections.length) return;
    saveTabScroll();
    setChatTypeTab(sections[idx].key);
  };

  const onTouchStart = (e) => {
    const t = e.touches[0];
    touchStartX.current = t.clientX;
    touchStartY.current = t.clientY;
  };
  const onTouchEnd = (e) => {
    if (touchStartX.current === null) return;
    const t = e.changedTouches[0];
    const dx = t.clientX - touchStartX.current;
    const dy = t.clientY - (touchStartY.current || 0);
    touchStartX.current = null; touchStartY.current = null;
    if (Math.abs(dx) > 60 && Math.abs(dx) > Math.abs(dy) * 1.5) {
      if (dx < 0) goToSection(currentIdx + 1);
      else goToSection(currentIdx - 1);
    }
  };

  // MB options — restrict to handler's assigned_mbs if set, else full list
  const assigned = Array.isArray(user?.assigned_mbs) && user.assigned_mbs.length > 0
    ? user.assigned_mbs
    : (mbOptions && mbOptions.length ? mbOptions : ['MB7','MB8','MB9','MB10','MB11']);
  const mbFilterOptions = ['all', ...assigned];

  const archivedCount = (chats || []).filter(c => c.is_archived).length;
  const chatList = filteredChats || [];

  return (
    <div className="h-screen flex flex-col bg-white overflow-hidden" data-testid="mobile-whatsapp-handler-view">
      {/* SELECTION MODE HEADER */}
      {selectionMode ? (
        <div className="bg-[#00a884] text-white px-4 py-3 flex items-center justify-between flex-shrink-0">
          <div className="flex items-center gap-3">
            <button onClick={cancelSelection} className="p-1 hover:bg-white/10 rounded-full">
              <span className="text-2xl leading-none">×</span>
            </button>
            <span className="font-semibold">{selectedRooms.length} selected</span>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={handleToggleArchive} disabled={selectedRooms.length === 0}
              className="bg-white/20 hover:bg-white/30 px-3 py-1 rounded text-sm disabled:opacity-50">
              📦 {activeTab === 'archive' ? 'Unarchive' : 'Archive'}
            </button>
            <button onClick={() => setShowDeleteConfirm(true)} disabled={selectedRooms.length === 0}
              className="bg-red-500 hover:bg-red-600 px-3 py-1 rounded text-sm disabled:opacity-50">
              Delete
            </button>
          </div>
        </div>
      ) : activeTab === 'archive' ? (
      /* ARCHIVE HEADER */
      <div className="bg-white flex items-center gap-3 px-4 py-3 flex-shrink-0 border-b border-gray-100">
        <button
          onClick={() => {
            let prev = 'all';
            try {
              const saved = sessionStorage.getItem('hdl_tab_before_archive');
              if (saved && ['all','unread','favourites','groups'].includes(saved)) prev = saved;
              sessionStorage.removeItem('hdl_tab_before_archive');
            } catch (_) {}
            saveTabScroll();
            setActiveTab(prev);
          }}
          className="hover:bg-gray-100 p-1.5 rounded-full transition-colors"
          data-testid="handler-mobile-archive-back-btn"
        >
          <span className="text-2xl leading-none">←</span>
        </button>
        <div className="flex items-center gap-2">
          <span className="text-xl">📦</span>
          <h2 className="font-semibold text-gray-900 text-lg">
            Archived <span className="text-sm text-gray-500 font-normal">({(chats || []).filter(c => c.is_archived).length})</span>
          </h2>
        </div>
      </div>
      ) : (
      /* HEADER */
      <div className="bg-white px-4 pt-3 pb-2 flex-shrink-0 border-b border-gray-100">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2.5 min-w-0">
            <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-[#00a884] to-[#008f70] flex items-center justify-center flex-shrink-0 shadow-sm">
              <span className="text-white font-black text-[14px] tracking-tight">DWS</span>
            </div>
            <div className="min-w-0">
              <h1 className="text-[17px] font-bold text-gray-900 leading-tight">
                DWS <span className="text-[#00a884]">Support</span>
              </h1>
              <p className="text-[11px] text-gray-500 leading-tight">
                {currentSection.label} panel
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            {user?.role !== 'third_party_withdraw_handler' && <InstantAccessPW variant="light" />}
            {user?.role !== 'third_party_withdraw_handler' && <InstantAccessN variant="light" />}
            {user?.role !== 'third_party_withdraw_handler' && <InstantAccessPT variant="light" />}
            <InstantAccessG variant="light" />
            <div className="relative" ref={menuRef}>
              <button
                onClick={() => setShowMenu(v => !v)}
                className="p-2 hover:bg-gray-100 rounded-full text-gray-700"
                data-testid="handler-mobile-3dot-menu"
              >
                <DotsThreeVertical size={22} weight="bold" />
              </button>
              {showMenu && (
                <div className="absolute right-0 top-10 bg-white rounded-lg shadow-2xl border z-[100] w-56 py-1">
                  {/* Session-37: Handler 3-dot menu is strictly locked to
                      Settings + Logout. Every other capability funnels
                      through Settings (permission-gated). */}
                  <button onClick={() => { setShowMenu(false); navigate('/groups'); }} className="w-full px-4 py-2.5 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2" data-testid="handler-mobile-groups-btn">
                    <Users size={18} /> Groups
                  </button>
                  <button onClick={() => { setShowMenu(false); navigate('/handler-settings'); }} className="w-full px-4 py-2.5 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2" data-testid="handler-mobile-settings-btn">
                    <Gear size={18} /> Settings
                  </button>
                  {/* Issue 14b — working Refresh (mirrors HandlerDashboard's
                      refreshTick bump via the onRefresh prop). */}
                  <button onClick={async () => { setShowMenu(false); toast.info('Refreshing...'); if (onRefresh) await onRefresh(); }} className="w-full px-4 py-2.5 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2" data-testid="handler-mobile-refresh-btn">
                    <ArrowClockwise size={18} /> Refresh
                  </button>
                  <div className="border-t my-1" />
                  <button onClick={() => { setShowMenu(false); handleLogout(); }} className="w-full px-4 py-2.5 text-left text-sm text-red-600 hover:bg-red-50 flex items-center gap-2" data-testid="handler-mobile-logout-btn">
                    <SignOut size={18} /> Logout
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Search */}
        <div className="mt-2.5 relative">
          <MagnifyingGlass size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search"
            className="w-full bg-gray-100 rounded-full pl-11 pr-4 py-2.5 text-sm text-gray-800 focus:outline-none placeholder:text-gray-500"
            data-testid="handler-mobile-search"
          />
        </div>
      </div>
      )}

      {/* Section dots (deposit_handler only, hidden in archive) */}
      {isDepositHandler && activeTab !== 'archive' && (
        <div className="flex items-center justify-center gap-1.5 py-1 flex-shrink-0">
          {sections.map((s, i) => (
            <button
              key={s.key}
              onClick={() => goToSection(i)}
              className={`h-1.5 rounded-full transition-all ${i === currentIdx ? 'w-6 bg-[#00a884]' : 'w-1.5 bg-gray-300'}`}
              data-testid={`handler-section-dot-${s.key}`}
            />
          ))}
        </div>
      )}

      {/* Filter chips — hidden in archive + hidden on Enquiry slide (InquiryPanel has its own filter) */}
      {activeTab !== 'archive' && !isInquirySection && (
      <div className="px-3 pb-2 flex gap-2 overflow-x-auto flex-shrink-0 no-scrollbar">
        <div className="relative flex-shrink-0" ref={mbRef}>
          <button
            onClick={(e) => {
              e.stopPropagation();
              const rect = e.currentTarget.getBoundingClientRect();
              setMbMenuPos({ top: rect.bottom + 6, left: rect.left });
              setActiveTab('all');
              setMbOpen(v => !v);
            }}
            className={`px-3.5 py-1.5 rounded-full text-sm whitespace-nowrap flex items-center gap-1 transition-colors ${
              activeTab === 'all' ? 'bg-[#d9fdd3] text-[#00a884] font-semibold' : 'text-gray-700 border border-gray-300'
            }`}
            data-testid="handler-mobile-filter-all"
          >
            {mbFilter === 'all' ? 'All' : mbFilter}
            <span className="text-[10px]">▾</span>
          </button>
          {mbOpen && (
            <div
              className="fixed z-[80] bg-white rounded-xl shadow-2xl border border-gray-200 py-1 min-w-[140px] max-h-[70vh] overflow-y-auto"
              style={{ top: mbMenuPos.top, left: mbMenuPos.left }}
              data-testid="handler-mobile-mb-menu"
            >
              {mbFilterOptions.map(opt => (
                <button
                  key={opt}
                  onClick={() => { setMbFilter(opt); setMbOpen(false); }}
                  className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-50 ${
                    mbFilter === opt ? 'text-[#00a884] font-semibold bg-[#d9fdd3]/40' : 'text-gray-700'
                  }`}
                  data-testid={`handler-mobile-mb-opt-${opt}`}
                >
                  {opt === 'all' ? 'All MBs' : opt}
                </button>
              ))}
            </div>
          )}
        </div>
        {FILTERS.filter(f => f.key !== 'all').map(f => {
          const active = activeTab === f.key;
          // Point 60 — chip badges count unread CHATS (not messages) and are
          // already scoped to the current MB filter / assigned_mbs by the
          // parent (HandlerDashboard.js).
          const badge = f.key === 'unread' ? unreadTabBadge
            : f.key === 'favourites' ? favouritesTabBadge
            : f.key === 'groups' ? groupsTabBadge
            : 0;
          return (
            <button
              key={f.key}
              onClick={() => { saveTabScroll(); (f.key === 'groups' && user?.role !== 'third_party_withdraw_handler') ? navigate('/groups') : setActiveTab(f.key); }}
              className={`px-3.5 py-1.5 rounded-full text-sm whitespace-nowrap flex items-center gap-1.5 transition-colors ${
                active ? 'bg-[#d9fdd3] text-[#00a884] font-semibold' : 'text-gray-700 border border-gray-300'
              }`}
              data-testid={`handler-mobile-filter-${f.key}`}
            >
              {f.label}
              {badge > 0 && (
                <span className="bg-red-500 text-white text-[10px] font-bold rounded-full min-w-[16px] h-4 px-1 flex items-center justify-center">
                  {badge > 99 ? '99+' : badge}
                </span>
              )}
            </button>
          );
        })}
        {/* Session-48i: Enquiry sub-tab REMOVED — Enquiry is now the 4th
            top-level slide tab (Deposit / Withdrawal / Support / Enquiry). */}
      </div>
      )}

      {/* Chat list */}
      <div ref={listRef} onTouchStart={onTouchStart} onTouchEnd={onTouchEnd} className="flex-1 overflow-y-auto">
        {isInquirySection ? (
          <InquiryPanel user={user} onOpenChat={(rid) => handleChatClick({ room_id: rid, chat_type: 'inquiry' })} />
        ) : (<>
        {archivedCount > 0 && activeTab !== 'unread' && activeTab !== 'archive' && (
          <div
            className="flex items-center gap-4 px-4 py-3 hover:bg-gray-50 active:bg-gray-100 cursor-pointer border-b border-gray-100"
            onClick={() => {
              try { sessionStorage.setItem('hdl_tab_before_archive', activeTab); } catch (_) {}
              saveTabScroll();
              setActiveTab('archive');
            }}
          >
            <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center flex-shrink-0">
              <span className="text-xl">📦</span>
            </div>
            <div className="flex-1">
              <div className="font-medium text-gray-800">Archived</div>
              <div className="text-xs text-gray-500 mt-0.5">{archivedCount}</div>
            </div>
          </div>
        )}
        {chatList.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-gray-400">
            <ChatCircleDots size={48} weight="light" />
            <p className="mt-3 text-sm">No chats yet</p>
          </div>
        ) : (() => {
          // Session-38 perf: shared row renderer so the classic `.map()`
          // path (< 100 chats) and the virtualized path (>= 100 chats) emit
          // identical JSX. `styleOverride` carries react-window's absolute
          // positioning when we're in virtual mode.
          const renderRow = (chat, styleOverride) => {
            const isGroup = activeTab === 'groups';
            const rid = isGroup ? chat.id : chat.room_id;
            const name = isGroup ? (chat.name || 'Group') : (chat.customer?.name || chat.customer_name || 'Chat');
            const paletteIdx = (name.charCodeAt(0) || 0) % AVATAR_PALETTE.length;
            const pal = AVATAR_PALETTE[paletteIdx];
            const initial = (name.trim()[0] || 'C').toUpperCase();
            const unread = unreadCounts[rid] || 0;
            const time = formatChatTime(chat.last_message?.timestamp || chat.last_message_time || chat.updated_at);
            const previewText = isGroup
              ? (chat.description || 'Group chat')
              : formatLastMessagePreview(chat.last_message);
            const preview = truncatePreview(previewText);
            const profilePic = isGroup ? chat.profile_pic : chat.customer?.profile_pic;
            const picUrl = profilePic
              ? (profilePic.startsWith('http') ? profilePic : `${API}${profilePic}`)
              : null;

            const isSelected = selectionMode && selectedRooms?.includes(rid);
            return (
              <div
                key={rid}
                style={{ ...(styleOverride || {}), WebkitUserSelect: 'none', userSelect: 'none', WebkitTouchCallout: 'none' }}
                onClick={() => selectionMode ? toggleRoomSelection(rid) : _openChat(chat)}
                onTouchStart={(e) => !selectionMode && handleLongPressStart && handleLongPressStart(rid, e)}
                onTouchEnd={handleLongPressEnd}
                onTouchMove={handleTouchMove}
                onMouseDown={(e) => !selectionMode && handleLongPressStart && handleLongPressStart(rid, e)}
                onMouseUp={handleLongPressEnd}
                onMouseLeave={handleLongPressEnd}
                className={`flex items-center gap-3 px-4 py-3 cursor-pointer border-b border-gray-100 transition-colors ${
                  isSelected ? 'bg-[#d9fdd3]/40' : 'hover:bg-gray-50 active:bg-gray-100'
                }`}
              >
                <div
                  className="w-14 h-14 rounded-full flex-shrink-0 overflow-hidden flex items-center justify-center text-[19px] font-semibold"
                  style={picUrl ? {
                    backgroundImage: `url(${picUrl})`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                  } : { backgroundColor: pal.bg, color: pal.fg }}
                >
                  {!picUrl && initial}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-baseline justify-between gap-2">
                    <div className="flex items-center gap-1.5 min-w-0 flex-1">
                      <div className="font-semibold text-[16px] text-gray-900 truncate">{truncateName(name)}</div>
                      {!isGroup && <MBSetupBadge value={chat.customer?.mb_setup} size="xs" />}
                    </div>
                    <div className={`text-[11px] flex-shrink-0 ${unread > 0 ? 'text-[#00a884] font-semibold' : 'text-gray-500'}`}>{time}</div>
                  </div>
                  <div className="flex items-center justify-between gap-2 mt-0.5">
                    <p className="text-[13.5px] text-gray-500 truncate flex-1 min-w-0">{preview}</p>
                    {unread > 0 && (
                      <span className="bg-[#00a884] text-white text-[11px] font-bold rounded-full min-w-[20px] h-5 px-1.5 flex items-center justify-center flex-shrink-0">
                        {unread > 99 ? '99+' : unread}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            );
          };

          // Session-38 perf: virtualize when > 100 rows. Below that, the
          // classic renderer is faster and simpler.
          const VIRTUALIZE_THRESHOLD = 100;
          const MOBILE_ROW_HEIGHT = 78; // 14*4 (avatar 56) + py-3 (24) - border
          if (chatList.length > VIRTUALIZE_THRESHOLD) {
            return (
              <div style={{ height: '100%', minHeight: 300 }} data-testid="handler-mobile-chatlist-virtualized">
                <AutoSizer
                  renderProp={({ height, width }) => (
                    <VirtualList
                      rowCount={chatList.length}
                      rowHeight={MOBILE_ROW_HEIGHT}
                      rowComponent={({ index, style }) => renderRow(chatList[index], style)}
                      rowProps={{}}
                      overscanCount={6}
                      style={{ height: height || 600, width: width || '100%' }}
                      defaultHeight={height || 600}
                    />
                  )}
                />
              </div>
            );
          }
          return chatList.map((chat) => renderRow(chat));
        })()}
        </>)}
      </div>

      {/* Delete Confirmation Modal — for handler this triggers a permission
          request instead of an actual delete (backend enforces admin-only). */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[100] p-4" onClick={() => setShowDeleteConfirm(false)}>
          <div className="bg-white rounded-xl p-5 max-w-sm w-full shadow-2xl" onClick={(e) => e.stopPropagation()} data-testid="mobile-handler-delete-confirm">
            <h3 className="text-base font-bold mb-2 text-gray-800">Send Delete Request?</h3>
            <p className="text-sm text-gray-600 mb-4">
              Handlers cannot delete chats directly. This will send a permission request to admin for <b>{selectedRooms.length}</b> chat{selectedRooms.length > 1 ? 's' : ''}. Admin will approve or reject it from their Settings.
            </p>
            <div className="flex gap-2">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium hover:bg-gray-50"
                data-testid="mobile-handler-delete-cancel"
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteRooms}
                className="flex-1 px-4 py-2 bg-orange-500 hover:bg-orange-600 text-white rounded-lg text-sm font-semibold"
                data-testid="mobile-handler-delete-request-btn"
              >
                Send Request
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MobileWhatsAppHandlerView;
